class FarloUiRevolverCarousel {
	constructor(element, options = {}) {
		this.element = element;
		this.slides = Array.from(element.children);
		this.currentIndex = 0;
		this.startX = 0;
		this.dragStartX = 0;
		this.dragEndX = 0;
		this.visibleSlides = 1;
		this.totalVisibleSets = 0;
		this.isDragging = false;
		this.hasDragged = false;
		this.autoplayInterval = null;

		// Default options
		this.defaultOptions = {
			arrows: true,
			auto: false,
			autoDuration: 3000,
			fade: false,
			lazyLoad: false,
			loop: true,
			pagination: true,
			swipe: true,
			thumbnails: false
		};

		// Get options from data attributes
		const dataOptions = this.getDataOptions();
		this.options = { ...this.defaultOptions, ...dataOptions, ...options };

		this.handleKeyboardInput = this.handleKeyboardInput.bind(this);

		this.init();
	}

	getDataOptions() {
		const dataOptions = {};
		const auto = this.element.getAttribute('data-autoplay');
		const autoDuration = this.element.getAttribute('data-autoplay-duration');
		const mode = this.element.getAttribute('data-mode');
		const pagination = this.element.getAttribute('data-pagination');
		const arrows = this.element.getAttribute('data-arrows');
		const thumbnails = this.element.getAttribute('data-thumbnails');

		if (auto !== null) {
			dataOptions.auto = auto === 'true';
		}
		if (autoDuration !== null) {
			dataOptions.autoDuration = parseInt(autoDuration, 10);
		}
		if (mode !== null) {
			dataOptions.mode = mode;
		}
		if (pagination !== null) {
			dataOptions.pagination = pagination === 'true';
		}
		if (arrows !== null) {
			dataOptions.arrows = arrows === 'true';
		}
		if (thumbnails !== null) {
			dataOptions.thumbnails = thumbnails === 'true';
		}

		return dataOptions;
	}

	init() {
		if (this.element.classList.contains('farlo-ui-revolver-carousel--loaded')) {
			return;
		}

		this.track = document.createElement('div');
		this.track.className = 'farlo-ui-revolver-carousel__cylinder';
		this.element.classList.add('farlo-ui-revolver-carousel', 'farlo-ui-revolver-carousel--loaded');

		this.slides.forEach((slide) => {
			slide.classList.add('farlo-ui-revolver-carousel__chamber');
			this.track.appendChild(slide);
		});

		this.element.appendChild(this.track);

		this.calculateVisibleSlides();
		this.updateTotalVisibleSets();
		this.updateCarousel();

		if (this.options.pagination) {
			this.createPagination();
		}
		if (this.options.thumbnails) {
			this.createThumbnails();
		}
		if (this.options.arrows) {
			this.createArrows();
		}
		if (this.options.fade) {
			this.applyFadeEffect();
		}
		if (this.options.auto) {
			this.startAutoplay();
		}

		this.addEventListeners();
		this.preventLinkClicksDuringDrag();
	}

	lazyLoadImage(slide) {
		const img = slide.querySelector('img');
		if (img) {
			const dataSrc = img.getAttribute('data-src');
			if (dataSrc) {
				img.setAttribute('src', dataSrc);
				img.removeAttribute('data-src');
			}
		}
	}

	createThumbnails() {
		let thumbnailContainer = this.element.querySelector('.farlo-ui-revolver-carousel__thumbnails');

		if (!thumbnailContainer && this.slides.length > 1) {
			thumbnailContainer = document.createElement('div');
			thumbnailContainer.className = 'farlo-ui-revolver-carousel__thumbnails';
			this.element.appendChild(thumbnailContainer);
		}

		if (thumbnailContainer) {
			thumbnailContainer.innerHTML = '';
			this.slides.forEach((slide, index) => {
				const thumbnailWrapper = document.createElement('div');
				thumbnailWrapper.className = 'farlo-ui-revolver-carousel__thumbnail-wrapper';

				const thumbnail = document.createElement('img');
				const img = slide.querySelector('img');
				thumbnail.src = img ? img.src : '';
				thumbnail.className = 'farlo-ui-revolver-carousel__thumbnail';
				thumbnail.setAttribute('aria-label', `Slide ${index + 1}`);
				thumbnail.addEventListener('click', () => this.goToSlide(index));

				thumbnailWrapper.appendChild(thumbnail);
				thumbnailContainer.appendChild(thumbnailWrapper);
			});

			this.updateThumbnailSizes();
			window.addEventListener('resize', this.updateThumbnailSizes.bind(this));
		}
	}

	createPagination() {
		let paginationContainer = this.element.querySelector('.farlo-ui-revolver-carousel__pagination');

		if (!paginationContainer && this.totalVisibleSets > 1) {
			paginationContainer = document.createElement('div');
			paginationContainer.className = 'farlo-ui-revolver-carousel__pagination';
			this.element.appendChild(paginationContainer);
		}

		if (paginationContainer) {
			paginationContainer.innerHTML = '';
			for (let i = 0; i < this.totalVisibleSets; i ++) {
				const dot = document.createElement('button');
				dot.className = 'farlo-ui-revolver-carousel__pagination-sightdot';
				dot.setAttribute('aria-label', `Group ${ i + 1 }`);
				dot.addEventListener('click', () => this.goToSlide(i));
				paginationContainer.appendChild(dot);
			}

			this.updatePaginationDots();
			this.setPaginationOffsetVariables(paginationContainer);
		}
	}

	setPaginationOffsetVariables(paginationContainer) {
		const paginationRect = paginationContainer.getBoundingClientRect();
		const offsetLeft = paginationRect.left;
		const offsetTop = paginationRect.top;
		const offsetBottom = paginationRect.bottom;
		const offsetRight = paginationRect.right;
		const height = paginationRect.height;

		this.element.style.setProperty('--farlo-ui-revolver-carousel-pagination-offset-left', `${ offsetLeft }px`);
		this.element.style.setProperty('--farlo-ui-revolver-carousel-pagination-offset-top', `${ offsetTop }px`);
		this.element.style.setProperty('--farlo-ui-revolver-carousel-pagination-offset-bottom', `${ offsetBottom }px`);
		this.element.style.setProperty('--farlo-ui-revolver-carousel-pagination-offset-right', `${ offsetRight }px`);
		this.element.style.setProperty('--farlo-ui-revolver-carousel-pagination-height', `${ height }px`);
	}

	createArrows() {
		const prevArrow = document.createElement('button');
		prevArrow.className = 'farlo-ui-revolver-carousel__trigger farlo-ui-revolver-carousel__trigger--prev';
		prevArrow.setAttribute('aria-label', 'Previous Slide');
		prevArrow.innerHTML = '&lt;';
		prevArrow.addEventListener('click', () => this.prevSlide());

		const nextArrow = document.createElement('button');
		nextArrow.className = 'farlo-ui-revolver-carousel__trigger farlo-ui-revolver-carousel__trigger--next';
		nextArrow.setAttribute('aria-label', 'Next Slide');
		nextArrow.innerHTML = '&gt;';
		nextArrow.addEventListener('click', () => this.nextSlide());

		this.element.appendChild(prevArrow);
		this.element.appendChild(nextArrow);
	}

	calculateVisibleSlides() {
		if (this.slides.length > 0) {
			const carouselWidth = this.element.offsetWidth;
			const slideStyle = window.getComputedStyle(this.slides[ 0 ]);
			const slideWidth = this.slides[ 0 ].offsetWidth + parseInt(slideStyle.marginLeft, 10) + parseInt(slideStyle.marginRight, 10);
			this.visibleSlides = Math.round(carouselWidth / slideWidth);
		}
	}

	updateTotalVisibleSets() {
		this.totalVisibleSets = Math.ceil(this.slides.length / this.visibleSlides);
	}

	applyFadeEffect() {
		this.track.classList.add('farlo-ui-revolver-carousel__cylinder--smoke');
	}

	addEventListeners() {
		this.element.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true });
		this.element.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: true });
		this.element.setAttribute('tabindex', '0');
		this.element.addEventListener('focus', () => {
			this.element.addEventListener('keydown', this.handleKeyboardInput);
		});
		this.element.addEventListener('blur', () => {
			this.element.removeEventListener('keydown', this.handleKeyboardInput);
		});

		if (this.options.swipe) {
			this.element.addEventListener('mousedown', this.startDrag.bind(this));
			this.element.addEventListener('mousemove', this.dragging.bind(this));
			this.element.addEventListener('mouseup', this.endDrag.bind(this));
			this.element.addEventListener('mouseleave', this.endDrag.bind(this));
		}

		window.addEventListener('resize', () => {
			clearTimeout(this.resizeTimeout);
			this.resizeTimeout = setTimeout(() => {
				this.onResize();
			}, 250);
		});
	}

	handleKeyboardInput(event) {
		switch (event.key) {
			case 'ArrowLeft':
				this.prevSlide();
				break;
			case 'ArrowRight':
				this.nextSlide();
				break;
		}
	}

	handleTouchStart(event) {
		this.startX = event.touches[ 0 ].clientX;
	}

	handleTouchEnd(event) {
		const endX = event.changedTouches[ 0 ].clientX;
		if (this.startX - endX > 30) {
			this.nextSlide();
		} else if (this.startX - endX < -30) {
			this.prevSlide();
		}
	}

	startDrag(event) {
		event.preventDefault();
		this.isDragging = true;
		this.hasDragged = false; // Reset hasDragged flag
		this.dragStartX = event.pageX;
	}

	dragging(event) {
		if (!this.isDragging) {
			return;
		}
		this.dragEndX = event.pageX;
		if (Math.abs(this.dragStartX - this.dragEndX) > 5) {
			this.hasDragged = true;
		}
	}

	endDrag() {
		if (!this.isDragging) {
			return;
		}
		this.isDragging = false;
		const delta = this.dragStartX - this.dragEndX;
		if (this.hasDragged && Math.abs(delta) > 30) {
			delta > 0 ? this.nextSlide() : this.prevSlide();
		}
	}

	onResize() {
		this.calculateVisibleSlides();
		this.updateTotalVisibleSets();
		if (this.currentIndex >= this.totalVisibleSets) {
			this.currentIndex = this.totalVisibleSets - 1;
		}
		this.updateCarousel();
		if (this.options.pagination) {
			this.createPagination();
		}
	}

	goToSlide(index) {
		this.currentIndex = index;
		this.updateCarousel();
	}

	nextSlide() {
		this.currentIndex = this.currentIndex < this.totalVisibleSets - 1 ? this.currentIndex + 1 : (this.options.loop ? 0 : this.currentIndex);
		this.updateCarousel();
	}

	prevSlide() {
		this.currentIndex = this.currentIndex > 0 ? this.currentIndex - 1 : (this.options.loop ? this.totalVisibleSets - 1 : this.currentIndex);
		this.updateCarousel();
	}

	startAutoplay() {
		this.autoplayInterval = setInterval(() => {
			this.nextSlide();
		}, this.options.autoDuration);
	}

	stopAutoplay() {
		clearInterval(this.autoplayInterval);
	}

	updatePaginationDots() {
		const dots = this.element.querySelectorAll('.farlo-ui-revolver-carousel__pagination-sightdot');
		dots.forEach((dot, index) => {
			dot.classList.toggle('farlo-ui-revolver-carousel__pagination-sightdot--active', index === this.currentIndex);
		});
	}

	updateCarousel() {
		if (this.options.auto) {
			this.restartAutoplay();
		}

		let transformIndex = this.currentIndex * this.visibleSlides;

		if (this.currentIndex === this.totalVisibleSets - 1 && this.slides.length % this.visibleSlides !== 0) {
			transformIndex = this.slides.length - this.visibleSlides;
		}

		const transformValue = -(transformIndex * 100 / this.visibleSlides);
		this.track.style.transform = `translateX(${ transformValue }%)`;

		this.slides.forEach((slide, index) => {
			slide.classList.toggle('farlo-ui-revolver__chamber--active', index === this.currentIndex);
			slide.setAttribute('aria-hidden', index !== this.currentIndex);
			if (this.options.fade) {
				slide.style.opacity = this.options.fade && index === this.currentIndex ? '1' : '0';
			}
			if (this.options.lazyLoad && index === this.currentIndex) {
				this.lazyLoadImage(slide);
			}
		});

		this.updatePaginationDots();
	}

	updateThumbnailSizes() {
		const thumbnailContainer = this.element.querySelector('.farlo-ui-revolver-carousel__thumbnails');
		const thumbnailWrappers = Array.from(thumbnailContainer.children);
		const totalThumbnails = thumbnailWrappers.length;

		const containerWidth = thumbnailContainer.offsetWidth;
		const thumbnailWidth = containerWidth / totalThumbnails;

		thumbnailWrappers.forEach((wrapper) => {
			wrapper.style.width = `${ thumbnailWidth }px`;
		});
	}

	restartAutoplay() {
		this.stopAutoplay();
		this.startAutoplay();
	}

	preventLinkClicksDuringDrag() {
		this.slides.forEach((slide) => {
			const links = slide.querySelectorAll('a, button');
			links.forEach((link) => {
				link.addEventListener('click', (event) => {
					if (this.isDragging && !this.hasDragged) {
						event.preventDefault();
					}
				});
			});
		});
	}
}

export default FarloUiRevolverCarousel;
